import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateEmail } from '../../utils/validation';
import { ERROR_CODES } from '../../utils/errorMessages';
import AuthLayout from '../../components/Layout/Auth/AuthLayout';
import { CONSTS } from '../../const';
import LoginContent from './LoginContent';
import LoginCard from './LoginCard';
import styled, { createGlobalStyle } from 'styled-components';
import { getErrMsgFromResp, handleErrorToast } from '../../utils/errorHandler';
import { rsaencrypt } from '../../utils/encryptpassword';
import { graphqlClient } from '../../utils/client/graphql';
import {
  INIT_MUTATION,
  LOGIN_MUTATION,
  IDENTIFY_LOGIN_METHOD_MUTATION,
  CHECK_EXISTING_SESSION_QUERY,
} from '../../graphql/queries';
const GlobalStyle = createGlobalStyle`
body{
  background: #E9F7FF;
}
`;
export const handleLoginSuccess = (data) => {
  if (data?.redirectionData?.viewIdentifier && data?.accessToken) {
    const dataToPass = {
      accessToken: data.accessToken,
    };

    const url = data?.redirectionData?.viewIdentifier;

    // Get allowed domains from environment variable or use default QA domains
    const allowedDomainsEnv = process.env.REACT_APP_ALLOWED_DOMAINS;
    const allowedDomains = allowedDomainsEnv
      ? allowedDomainsEnv.split(',')
      : [];

    // Check if the URL exists in allowed domains
    const isAllowedDomain = allowedDomains.some((domain) => domain == url);

    if (isAllowedDomain) {
      const encodedData = btoa(JSON.stringify(dataToPass));
      console.log('in login success - redirecting to:', url);
      // const redirectUrl = `${url}?data=${encodedData}`;
      const redirectUrl = `${url}`;

      window.location.href = redirectUrl;
    } else {
      console.error('Redirect URL not allowed:', url);
    }
  }
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [gpkanim, setGpkanim] = useState(false);
  const [loading, setLoading] = useState(false);
  const [identifyLoading, setIdentifyLoading] = useState(false);
  const [ssoRedirecting, setSsoRedirecting] = useState(false);
  const [loginStep, setLoginStep] = useState('email'); // 'email' | 'password'
  const [initLoading, setInitLoading] = useState(true);
  const [loginDisabled, setLoginDisabled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!gpkanim && (email || password)) {
      setGpkanim(true);
    } else if (gpkanim && !email && !password) {
      setGpkanim(false);
    }
  }, [email, password]);
  useEffect(() => {
    setInitLoading(true);
    graphqlClient
      .request(CHECK_EXISTING_SESSION_QUERY)
      .then((data) => {
        const session = data?.checkExistingSession;
        if (session?.status === 'OK') {
          const viewIdentifier = session?.data?.redirection?.viewIdentifier;
          if (viewIdentifier) {
            const allowedDomainsEnv = process.env.REACT_APP_ALLOWED_DOMAINS;
            const allowedDomains = allowedDomainsEnv
              ? allowedDomainsEnv.split(',')
              : [];
            if (allowedDomains.some((domain) => domain === viewIdentifier)) {
              window.location.href = viewIdentifier;
              return;
            }
          }
        }
        // No active session — prime the init so the login button is ready.
        return graphqlClient
          .request(INIT_MUTATION)
          .catch((err) => console.log(err));
      })
      .catch((err) => {
        console.log(err);
        return graphqlClient
          .request(INIT_MUTATION)
          .catch((e) => console.log(e));
      })
      .finally(() => {
        setInitLoading(false);
      });
  }, []);

  const handleLogin = async () => {
    // Validate email
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.error);
      return;
    }

    // Basic password validation
    if (!password) {
      setError('Please enter your password');
      return;
    }

    try {
      setLoading(true);
      // Variables are passed as a thunk so a 428-triggered re-init can
      // re-evaluate them — `rsaencrypt` will pick up the freshly-issued
      // publicKey from the graphql client on the retry.
      const response = await graphqlClient.request(LOGIN_MUTATION, () => ({
        input: {
          email: email,
          password: rsaencrypt(password),
        },
      }));

      const { status, data, message, errorCode } = response?.login || {};

      switch (errorCode) {
        case ERROR_CODES.OTP_REQUIRED:
          if (data?.sourceType == 'MOBILE') {
            navigate('/login', {
              state: {
                screen: 'otp',
                mobile: data?.source,
                otpKey: data.otpKey,
                resendTime: data.resendReadyTime,
                sourceState: data?.sourceType,
                user: data?.user,
              },
            });
          } else {
            navigate('/login', {
              state: {
                screen: 'otp',
                email: data?.source,
                otpKey: data.otpKey,
                resendTime: data.resendReadyTime,
                sourceState: data?.sourceType,
              },
            });
          }
          break;

        case ERROR_CODES.TRY_AFTER_SOME_TIME:
          setLoginDisabled(true);
          setTimeout(() => setLoginDisabled(false), 300000); // 5 minutes
          break;
        case 'SET_PASSWORD':
          navigate('/signup', {
            state: {
              screen: 'set-password',
              email: email,
              verificationkey: data.otpKey,
            },
          });
          break;
        default:
          if (status === CONSTS.OK) {
            setLoading(false);
            handleLoginSuccess(data);
          } else if (status === CONSTS.VER || status === CONSTS.ERR) {
            throw response;
          } else {
            console.log('Default case trigger');
          }
      }
    } catch (err) {
      console.log('Error status code');
      setError(getErrMsgFromResp(err));
    } finally {
      setLoading(false);
    }
  };

  const handleIdentify = async () => {
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.error);
      return;
    }
    try {
      setError('');
      setIdentifyLoading(true);
      const response = await graphqlClient.request(
        IDENTIFY_LOGIN_METHOD_MUTATION,
        { email }
      );
      const { status, data } = response?.identifyLoginMethod || {};
      if (
        status === CONSTS.OK &&
        data?.authMethod === 'SSO' &&
        data?.redirectUrl
      ) {
        setSsoRedirecting(true);
        window.location.href = data.redirectUrl;
        return;
      } else if (status === CONSTS.OK && data?.authMethod === 'PASSWORD') {
        setLoginStep('password');
      } else if (status === CONSTS.VER || status === CONSTS.ERR) {
        throw response;
      }
    } catch (err) {
      setError(getErrMsgFromResp(err));
    } finally {
      setIdentifyLoading(false);
    }
  };

  useEffect(() => {
    handleErrorToast(error);
    setTimeout(() => {
      setError('');
    }, 500);
  }, [error]);

  return (
    <StyledAuthLayout leftContent={<LoginContent anim={gpkanim} />}>
      <GlobalStyle />
      <LoginCard
        loading={loading || initLoading}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        error={error}
        handleLogin={handleLogin}
        handleIdentify={handleIdentify}
        loginStep={loginStep}
        setLoginStep={setLoginStep}
        identifyLoading={identifyLoading}
        ssoRedirecting={ssoRedirecting}
        loginDisabled={loginDisabled}
      />
    </StyledAuthLayout>
  );
};

const StyledAuthLayout = styled(AuthLayout)`
  background: red !important;
`;
export default Login;
